import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bike, ChefHat, PhoneOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { unlockAudio, playAlertSound, vibrate } from "@/lib/pushNotifications";
import { consumeNativeLaunchAction, dismissAlert, configureNativeAlerts } from "@/utils/incomingAlerts";

const POLL_MS = 8000;
const RING_EVERY_MS = 3000;

/* Écran d'alerte plein écran façon « appel entrant » (restaurant : nouvelle commande ;
   livreur : offre de course). Sonne en boucle jusqu'à réponse ou expiration. Sources :
   /alerts/active (sondage), push reçu au premier plan, action sur la notification (iOS),
   action sur l'écran natif (Android). */
export default function IncomingAlertOverlay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [alert, setAlert] = useState(null);
  const busyRef = useRef(false);
  const role = user?.role;
  const eligible = role === "restaurant" || role === "driver";

  // Déverrouillage audio au 1er geste (iOS / navigateurs)
  useEffect(() => {
    const unlock = () => unlockAudio();
    document.addEventListener("pointerdown", unlock, { once: true, passive: true });
    document.addEventListener("touchend", unlock, { once: true, passive: true });
    return () => { document.removeEventListener("pointerdown", unlock); document.removeEventListener("touchend", unlock); };
  }, []);

  const load = useCallback(async () => {
    if (!eligible) return;
    try {
      const { data } = await apiClient.get("/alerts/active");
      // Sur /driver, l'écran d'offre prioritaire existant gère déjà l'offre (et son alarme)
      const next = (data || []).find((a) => !(a.kind === "priority_offer" && pathname === "/driver")) || null;
      setAlert((prev) => (prev && next && prev.alert_id === next.alert_id ? prev : next));
    } catch (e) { /* hors ligne : on garde l'état courant */ }
  }, [eligible, pathname]);

  // Actions (Voir / Ignorer / J'y vais / Passer) — communes à l'écran web, aux boutons iOS et à l'écran Android
  const act = useCallback(async (a, action) => {
    if (!a || busyRef.current) return;
    busyRef.current = true;
    setAlert(null);
    try {
      if (a.kind === "priority_offer" && action === "accept") {
        try {
          await apiClient.patch(`/orders/${a.order_id}/status`, { status: "delivering" });
          toast.success(t("alerts.accepted"));
        } catch (e) {
          toast.error(e?.response?.status === 409 ? t("ddrv.toastTooLate") : (e?.response?.data?.detail || t("ddrv.toastUpdateFail")));
        }
        navigate("/driver");
      } else if (a.kind === "priority_offer" && action === "dismiss") {
        apiClient.post(`/orders/${a.order_id}/decline-offer`).catch(() => {});
      } else if (action === "accept") {
        navigate(a.url || "/dashboard/restaurant?tab=today");
      }
      await dismissAlert(a.alert_id);
    } finally {
      busyRef.current = false;
    }
  }, [navigate, t]);

  // Sondage + événements push
  useEffect(() => {
    if (!eligible) { setAlert(null); return undefined; }
    load();
    const timer = setInterval(load, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") { load(); checkLaunchAction(); configureNativeAlerts(); } };
    const onRing = () => load();
    const onAction = (e) => {
      const d = e.detail || {};
      const a = { alert_id: d.alert_id, kind: d.kind, order_id: d.order_id, url: d.url };
      const id = d.action;
      if (id === "DISMISS" || id === "DECLINE") act(a, "dismiss");
      else act(a, "accept"); // VIEW / ACCEPT / tap → ouvrir
    };
    const checkLaunchAction = async () => {
      const la = await consumeNativeLaunchAction();
      if (la) act(la, la.action === "dismiss" ? "dismiss" : "accept");
    };
    checkLaunchAction();
    configureNativeAlerts();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("neldaari:alert", onRing);
    window.addEventListener("neldaari:alert-action", onAction);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("neldaari:alert", onRing);
      window.removeEventListener("neldaari:alert-action", onAction);
    };
  }, [eligible, load, act]);

  // Sonnerie en boucle + expiration
  const alertId = alert?.alert_id;
  useEffect(() => {
    if (!alertId) return undefined;
    const ring = () => { playAlertSound("priority"); vibrate([450, 250, 450]); };
    ring();
    const timer = setInterval(ring, RING_EVERY_MS);
    const ms = Math.max(0, new Date(alert.expires_at).getTime() - Date.now());
    const expiry = setTimeout(() => setAlert(null), ms);
    return () => { clearInterval(timer); clearTimeout(expiry); };
  }, [alertId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!alert) return null;
  const driver = alert.kind === "priority_offer";
  return (
    <div className="fixed inset-0 z-[90] bg-[#141210] text-white flex flex-col items-center justify-center px-7 py-10 text-center"
         data-testid="incoming-alert-overlay" role="alertdialog" aria-live="assertive">
      <div className="absolute inset-0 pointer-events-none opacity-[0.07]"
           style={{ backgroundImage: "radial-gradient(circle at 50% 30%, #fff 0, transparent 60%)" }} />
      <div className={`relative w-24 h-24 rounded-full flex items-center justify-center mb-6 ${driver ? "bg-brand-red" : "bg-brand-green"}`}>
        <span className={`absolute inset-0 rounded-full animate-ping opacity-40 ${driver ? "bg-brand-red" : "bg-brand-green"}`} />
        {driver ? <Bike className="w-11 h-11 relative" /> : <ChefHat className="w-11 h-11 relative" />}
      </div>
      <div className="text-xs uppercase tracking-[0.3em] text-[#F4C542] font-semibold" data-testid="incoming-alert-kicker">
        {driver ? t("alerts.kickerDriver") : t("alerts.kickerResto")}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-3" data-testid="incoming-alert-title">{alert.title}</h1>
      <p className="text-base text-[#E8E2D6] mt-3 max-w-md leading-relaxed" data-testid="incoming-alert-body">{alert.body}</p>
      <Countdown until={alert.expires_at} />
      <p className="text-sm text-[#A89F92] mt-1">{driver ? t("alerts.hintDriver") : t("alerts.hintResto")}</p>
      <div className="grid grid-cols-2 gap-3 w-full max-w-md mt-10">
        <button onClick={() => act(alert, "dismiss")} data-testid="incoming-alert-dismiss"
                className="rounded-2xl bg-[#2A2724] hover:bg-[#3a3632] py-5 font-bold text-lg inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <PhoneOff className="w-5 h-5" /> {driver ? t("ddrv.btnSkip") : t("alerts.ignore")}
        </button>
        <button onClick={() => act(alert, "accept")} data-testid="incoming-alert-accept"
                className={`rounded-2xl py-5 font-extrabold text-lg inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform ${driver ? "bg-brand-red hover:bg-[#C81F2D]" : "bg-brand-green hover:bg-[#007A4A]"}`}>
          {driver ? <Bike className="w-5 h-5" /> : <Eye className="w-5 h-5" />} {driver ? t("ddrv.btnGo") : t("alerts.view")}
        </button>
      </div>
      {!Capacitor.isNativePlatform() && null}
    </div>
  );
}

function Countdown({ until }) {
  const calc = () => Math.max(0, Math.floor((new Date(until).getTime() - Date.now()) / 1000));
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    const timer = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(timer);
  }, [until]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`font-display text-5xl font-extrabold tabular-nums mt-8 ${left <= 15 ? "animate-pulse text-[#F4C542]" : ""}`} data-testid="incoming-alert-countdown">
      {left}s
    </div>
  );
}
